/**
 * NovelDetailPage - 小說詳情頁面
 *
 * 重構後使用 novel-detail/ 目錄下的元件:
 * - NovelInfoCard: 封面、標題、作者、統計、操作按鈕
 * - ChapterList: 章節列表 (含分組、編輯模式)
 * - SeriesSection: 同系列書籍
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Loading } from '../components/Loading';
import { CrawlProgress } from '../components/CrawlProgress';
import { Toast, ToastType } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';
import { NovelSeriesManagerModal } from '../components/admin/NovelSeriesManagerModal';
import { NovelEditorModal } from '../components/admin/NovelEditorModal';
import { ChapterContentEditorModal } from '../components/admin/ChapterContentEditorModal';
import { AddChaptersModal } from '../components/admin/AddChaptersModal';
import { SyncChaptersModal } from '../components/admin/SyncChaptersModal';
import { NovelInfoCard, ChapterList, SeriesSection } from '../components/novel-detail';
import type { Novel, Chapter, UserReadingProgress, SeriesDetail } from '../services/types';

export function NovelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // 基本資料狀態
  const [novel, setNovel] = useState<Novel | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [userProgress, setUserProgress] = useState<UserReadingProgress | null>(null);
  const [seriesDetail, setSeriesDetail] = useState<SeriesDetail | null>(null);

  // 下載與抓取狀態
  const [downloading, setDownloading] = useState(false);
  const [recrawling, setRecrawling] = useState(false);
  const [recrawlingChapterId, setRecrawlingChapterId] = useState<string | null>(null);
  const [skippingChapterId, setSkippingChapterId] = useState<string | null>(null);

  // Modal 狀態
  const [showSeriesManager, setShowSeriesManager] = useState(false);
  const [showNovelEditor, setShowNovelEditor] = useState(false);
  const [editingContentChapter, setEditingContentChapter] = useState<Chapter | null>(null);
  const [showAddChapters, setShowAddChapters] = useState(false);
  const [showSyncChapters, setShowSyncChapters] = useState(false);

  // 編輯模式狀態
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedChapterIds, setSelectedChapterIds] = useState<Set<string>>(new Set());

  // Toast & Confirm 狀態
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    variant?: 'danger' | 'warning' | 'primary';
    onConfirm: () => void;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    setToast({ message, type });
  }, []);

  // 計算衍生資料
  const completedChapters = useMemo(
    () => chapters.filter((c) => c.status === 'completed'),
    [chapters]
  );
  const lastReadChapter = useMemo(() => {
    if (!userProgress?.chapter_id) return null;
    return chapters.find((c) => c.id === userProgress.chapter_id) || null;
  }, [userProgress, chapters]);
  const totalWords = useMemo(
    () => completedChapters.reduce((sum, ch) => sum + ch.word_count, 0),
    [completedChapters]
  );
  const seriesNovels = useMemo(() => seriesDetail?.novels || [], [seriesDetail]);
  const hasEmptyChapters = useMemo(
    () => chapters.some((c) => c.status === 'completed' && c.word_count === 0),
    [chapters]
  );

  // 資料載入
  const loadNovelData = useCallback(async () => {
    if (!id) return;
    try {
      const [novelData, allChapters, favoriteData, progressData] = await Promise.all([
        api.getNovel(id),
        api.getAllChapters(id),
        api.checkFavorite(id),
        api.getReadingProgress(id),
      ]);
      setNovel(novelData);
      setChapters(allChapters);
      setIsFavorited(favoriteData.isFavorited);
      setUserProgress(progressData);

      if (novelData.series?.id) {
        try {
          const detail = await api.getSeriesDetail(novelData.series.id);
          setSeriesDetail(detail);
        } catch (err) {
          console.error('Failed to load series detail:', err);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load novel');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) loadNovelData();
  }, [id, loadNovelData]);

  // 小說操作
  const handleDelete = useCallback(() => {
    if (!id) return;
    setConfirmModal({
      title: '刪除小說',
      message: '確定要刪除這本小說嗎？此操作無法復原。',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.deleteNovel(id);
          navigate('/novels');
        } catch (err) {
          showToast(err instanceof Error ? err.message : '刪除失敗', 'error');
        } finally {
          setConfirmLoading(false);
          setConfirmModal(null);
        }
      },
    });
  }, [id, navigate, showToast]);

  const handlePause = useCallback(async () => {
    if (!id) return;
    try {
      const updated = await api.pauseNovel(id);
      setNovel(updated);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '暫停失敗', 'error');
    }
  }, [id, showToast]);

  const handleResume = useCallback(async () => {
    if (!id) return;
    try {
      const updated = await api.resumeNovel(id);
      setNovel(updated);
    } catch (err) {
      showToast(err instanceof Error ? err.message : '恢復失敗', 'error');
    }
  }, [id, showToast]);

  const handleRecrawlEmpty = useCallback(() => {
    if (!id) return;
    const emptyCount = chapters.filter(
      (c) => c.status === 'completed' && c.word_count === 0
    ).length;
    if (emptyCount === 0) {
      showToast('沒有空白章節需要重新抓取', 'info');
      return;
    }
    setConfirmModal({
      title: '重新抓取空白章節',
      message: `確定要重新抓取 ${emptyCount} 個空白章節嗎？`,
      variant: 'warning',
      onConfirm: async () => {
        setConfirmLoading(true);
        setRecrawling(true);
        try {
          const result = await api.recrawlEmptyChapters(id);
          setNovel(result);
          showToast(`已排程重新抓取 ${result.recheduledCount} 個章節`, 'success');
          loadNovelData();
        } catch (err) {
          showToast(err instanceof Error ? err.message : '重新抓取失敗', 'error');
        } finally {
          setRecrawling(false);
          setConfirmLoading(false);
          setConfirmModal(null);
        }
      },
    });
  }, [id, chapters, showToast, loadNovelData]);

  const handleToggleFavorite = useCallback(async () => {
    if (!id || favoriteLoading) return;
    setFavoriteLoading(true);
    setIsFavorited(!isFavorited);
    try {
      if (isFavorited) {
        await api.removeFavorite(id);
      } else {
        await api.addFavorite(id);
      }
    } catch (err) {
      setIsFavorited(isFavorited);
      console.error('Toggle favorite failed:', err);
    } finally {
      setFavoriteLoading(false);
    }
  }, [id, isFavorited, favoriteLoading]);

  const handleDownload = useCallback(
    async (format: 'zip' | 'txt') => {
      if (!novel) return;
      setDownloading(true);
      try {
        const data = await api.exportNovel(novel.id);
        const filename = data.novel.title.replace(/[/\\:*?"<>|]/g, '_').trim();
        if (format === 'zip') {
          const zip = new JSZip();
          data.chapters.forEach((ch) => {
            const idx = String(ch.chapter_index).padStart(4, '0');
            const chapterFilename = `${idx}-${ch.title}.txt`.replace(/[/\\:*?"<>|]/g, '_');
            zip.file(chapterFilename, ch.content);
          });
          const blob = await zip.generateAsync({ type: 'blob' });
          saveAs(blob, `${filename}.zip`);
        } else {
          const content = data.chapters
            .map((ch) => `${'='.repeat(40)}\n${ch.title}\n${'='.repeat(40)}\n\n${ch.content}`)
            .join('\n\n\n');
          const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
          saveAs(blob, `${filename}.txt`);
        }
      } catch (err) {
        showToast(err instanceof Error ? err.message : '下載失敗', 'error');
      } finally {
        setDownloading(false);
      }
    },
    [novel, showToast]
  );

  // 章節操作
  const handleRecrawlChapter = useCallback(
    (chapter: Chapter) => {
      setConfirmModal({
        title: '重新抓取章節',
        message: `確定要重新抓取「第 ${chapter.chapter_index} 章 ${chapter.title}」嗎？`,
        variant: 'warning',
        onConfirm: async () => {
          setConfirmLoading(true);
          setRecrawlingChapterId(chapter.id);
          try {
            const result = await api.recrawlChapter(chapter.id);
            showToast(result.message, 'success');
            loadNovelData();
          } catch (err) {
            showToast(err instanceof Error ? err.message : '重新抓取章節失敗', 'error');
          } finally {
            setRecrawlingChapterId(null);
            setConfirmLoading(false);
            setConfirmModal(null);
          }
        },
      });
    },
    [showToast, loadNovelData]
  );

  const handleSkipChapter = useCallback(
    (chapter: Chapter) => {
      setConfirmModal({
        title: '跳過章節',
        message: `確定要跳過「第 ${chapter.chapter_index} 章 ${chapter.title}」嗎？此章節會被標記為失敗，讓整本小說可以結案。事後仍可透過「重新抓取」再試一次。`,
        variant: 'danger',
        onConfirm: async () => {
          setConfirmLoading(true);
          setSkippingChapterId(chapter.id);
          try {
            const result = await api.skipChapter(chapter.id);
            showToast(result.message, 'success');
            loadNovelData();
          } catch (err) {
            showToast(err instanceof Error ? err.message : '跳過章節失敗', 'error');
          } finally {
            setSkippingChapterId(null);
            setConfirmLoading(false);
            setConfirmModal(null);
          }
        },
      });
    },
    [showToast, loadNovelData]
  );

  const handleMoveChapter = useCallback(
    async (chapter: Chapter, direction: 'up' | 'down') => {
      if (!id) return;
      const currentIndex = completedChapters.findIndex((c) => c.id === chapter.id);
      if (currentIndex === -1) return;
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (targetIndex < 0 || targetIndex >= completedChapters.length) return;
      const currentChapter = completedChapters[currentIndex];
      const targetChapter = completedChapters[targetIndex];
      try {
        await api.reorderChapters(id, [
          { id: currentChapter.id, newIndex: targetChapter.chapter_index },
          { id: targetChapter.id, newIndex: currentChapter.chapter_index },
        ]);
        showToast('章節順序已更新', 'success');
        loadNovelData();
      } catch (err) {
        showToast(err instanceof Error ? err.message : '調整順序失敗', 'error');
      }
    },
    [id, completedChapters, showToast, loadNovelData]
  );

  const handleDeleteChapter = useCallback(
    (chapter: Chapter) => {
      setConfirmModal({
        title: '刪除章節',
        message: `確定要刪除「第 ${chapter.chapter_index} 章 ${chapter.title}」嗎？此操作無法復原。`,
        variant: 'danger',
        onConfirm: async () => {
          setConfirmLoading(true);
          try {
            await api.deleteChapter(chapter.id);
            showToast('章節已刪除', 'success');
            loadNovelData();
          } catch (err) {
            showToast(err instanceof Error ? err.message : '刪除失敗', 'error');
          } finally {
            setConfirmLoading(false);
            setConfirmModal(null);
          }
        },
      });
    },
    [showToast, loadNovelData]
  );

  const handleToggleChapterSelect = useCallback((chapterId: string) => {
    setSelectedChapterIds((prev) => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedChapterIds.size === completedChapters.length) {
      setSelectedChapterIds(new Set());
    } else {
      setSelectedChapterIds(new Set(completedChapters.map((c) => c.id)));
    }
  }, [selectedChapterIds.size, completedChapters]);

  const handleBatchDelete = useCallback(() => {
    if (!id || selectedChapterIds.size === 0) return;
    setConfirmModal({
      title: '批量刪除章節',
      message: `確定要刪除選取的 ${selectedChapterIds.size} 個章節嗎？此操作無法復原。`,
      variant: 'danger',
      onConfirm: async () => {
        setConfirmLoading(true);
        try {
          await api.batchDeleteChapters(id, Array.from(selectedChapterIds));
          showToast(`已刪除 ${selectedChapterIds.size} 個章節`, 'success');
          setSelectedChapterIds(new Set());
          setIsEditMode(false);
          loadNovelData();
        } catch (err) {
          showToast(err instanceof Error ? err.message : '批量刪除失敗', 'error');
        } finally {
          setConfirmLoading(false);
          setConfirmModal(null);
        }
      },
    });
  }, [id, selectedChapterIds, showToast, loadNovelData]);

  const handleExitEditMode = useCallback(() => {
    setIsEditMode(false);
    setSelectedChapterIds(new Set());
  }, []);

  // Loading & Error 狀態
  if (loading) {
    return <Loading text="載入小說中..." />;
  }

  if (error || !novel) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger-50 dark:bg-danger-900/20 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-danger-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <p className="text-danger-600 dark:text-danger-400 mb-4">{error || 'Novel not found'}</p>
        <Link to="/novels" className="btn btn-primary btn-md">
          返回書架
        </Link>
      </div>
    );
  }

  const showProgress = novel.status === 'pending' || novel.status === 'crawling';

  return (
    <div className="space-y-6">
      {/* 小說資訊卡片 */}
      <NovelInfoCard
        novel={novel}
        completedChapters={completedChapters}
        lastReadChapter={lastReadChapter}
        userProgress={userProgress}
        totalWords={totalWords}
        isFavorited={isFavorited}
        favoriteLoading={favoriteLoading}
        downloading={downloading}
        recrawling={recrawling}
        isAdmin={isAdmin}
        onToggleFavorite={handleToggleFavorite}
        onDownload={handleDownload}
        onPause={handlePause}
        onResume={handleResume}
        onDelete={handleDelete}
        onRecrawlEmpty={handleRecrawlEmpty}
        onShowNovelEditor={() => setShowNovelEditor(true)}
        onShowSeriesManager={() => setShowSeriesManager(true)}
        onShowSyncChapters={() => setShowSyncChapters(true)}
        hasEmptyChapters={hasEmptyChapters}
      />

      {/* 下載進度 */}
      {showProgress && id && (
        <div className="card">
          <div className="card-header">
            <h2 className="heading-section">下載進度</h2>
          </div>
          <div className="card-body">
            <CrawlProgress novelId={id} />
          </div>
        </div>
      )}

      {/* 同系列書籍 */}
      <SeriesSection currentNovel={novel} seriesNovels={seriesNovels} />

      {/* 章節列表 */}
      <ChapterList
        novel={novel}
        completedChapters={completedChapters}
        userProgressChapterId={userProgress?.chapter_id || null}
        isAdmin={isAdmin}
        isEditMode={isEditMode}
        selectedChapterIds={selectedChapterIds}
        recrawlingChapterId={recrawlingChapterId}
        skippingChapterId={skippingChapterId}
        onEditContent={setEditingContentChapter}
        onRecrawl={handleRecrawlChapter}
        onSkip={handleSkipChapter}
        onMoveChapter={handleMoveChapter}
        onDeleteChapter={handleDeleteChapter}
        onToggleChapterSelect={handleToggleChapterSelect}
        onSelectAll={handleSelectAll}
        onBatchDelete={handleBatchDelete}
        onExitEditMode={handleExitEditMode}
        onEnterEditMode={() => setIsEditMode(true)}
        onShowAddChapters={() => setShowAddChapters(true)}
      />

      {/* Modals */}
      {showSeriesManager && novel && (
        <NovelSeriesManagerModal
          novel={novel}
          isOpen={showSeriesManager}
          onClose={() => setShowSeriesManager(false)}
          onUpdate={loadNovelData}
        />
      )}

      {showNovelEditor && novel && (
        <NovelEditorModal
          novel={novel}
          isOpen={showNovelEditor}
          onClose={() => setShowNovelEditor(false)}
          onSave={async (data) => {
            await api.updateNovel(novel.id, data);
            await loadNovelData();
          }}
        />
      )}

      {editingContentChapter && novel && (
        <ChapterContentEditorModal
          novelId={novel.id}
          chapter={editingContentChapter}
          isOpen={true}
          onClose={() => setEditingContentChapter(null)}
          onSuccess={() => {
            setEditingContentChapter(null);
            showToast('章節內容已更新', 'success');
            loadNovelData();
          }}
        />
      )}

      {showAddChapters && novel && (
        <AddChaptersModal
          novelId={novel.id}
          existingChapterCount={completedChapters.length}
          isOpen={showAddChapters}
          onClose={() => setShowAddChapters(false)}
          onSuccess={() => {
            setShowAddChapters(false);
            showToast('章節已新增', 'success');
            loadNovelData();
          }}
        />
      )}

      {showSyncChapters && novel && (
        <SyncChaptersModal
          novelId={novel.id}
          novelTitle={novel.title}
          isOpen={showSyncChapters}
          onClose={() => setShowSyncChapters(false)}
          onSuccess={() => {
            setShowSyncChapters(false);
            showToast('新章節已開始同步', 'success');
            loadNovelData();
          }}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          isLoading={confirmLoading}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
